import React, { useEffect, useState, useCallback, useRef } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Music2, Mic2, Plus, ArrowLeft, Loader2, Upload, X, FileText } from "lucide-react";
import { useLang } from "@/context/LangContext";
import { Header } from "@/components/Header";
import { OtpGate } from "@/components/OtpGate";
import { VocalRecorder } from "@/components/VocalRecorder";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const inputStyle = { background: "var(--bg)", border: "2px solid var(--border)", color: "var(--text)" };
function fmtErr(d) { return typeof d === "string" ? d : Array.isArray(d) ? d.map((e) => e?.msg || "").join(" ") : "Something went wrong."; }

export default function WorkshopProject() {
  const { id } = useParams();
  const { t, lang } = useLang();
  const [proj, setProj] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(() => sessionStorage.getItem("sk_ws_token"));
  const [gate, setGate] = useState(false);
  const [pending, setPending] = useState(null);
  const [action, setAction] = useState(null); // 'melody' | 'sing' | 'upload'
  const [karaoke, setKaraoke] = useState(null);

  const load = useCallback(() => {
    axios.get(`${API}/workshop/projects/${id}`).then((r) => setProj(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, [id]);
  useEffect(() => { load(); }, [load]);

  const open = (type) => { if (token) setAction(type); else { setPending(type); setGate(true); } };
  const onVerified = (tok) => { sessionStorage.setItem("sk_ws_token", tok); setToken(tok); setGate(false); setAction(pending); setPending(null); };
  const onExpire = (type) => { sessionStorage.removeItem("sk_ws_token"); setToken(null); setAction(null); setPending(type); setGate(true); };

  if (loading) return <div className="min-h-screen grid place-items-center" style={{ background: "var(--bg)", color: "var(--text-muted)" }}><Loader2 className="animate-spin" /></div>;
  if (!proj) return <div className="min-h-screen grid place-items-center font-ui" style={{ background: "var(--bg)", color: "var(--text-muted)" }}>Not found. <Link to="/rooms/sindu-kamare/workshop" className="underline ml-2">Back</Link></div>;

  return (
    <div className="min-h-screen pb-16" style={{ background: "var(--bg)" }}>
      <Header />
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8">
        <Link to="/rooms/sindu-kamare/workshop" className="inline-flex items-center gap-2 font-ui text-sm font-semibold mb-4" style={{ color: "var(--accent)" }}><ArrowLeft size={16} /> {t("ws_nav")}</Link>

        <div className="rounded-3xl p-6 mb-6 sk-fade-up" style={{ background: "var(--card)", border: "2px solid var(--border)" }}>
          <div className="flex items-center gap-2 mb-2" style={{ color: "var(--accent)" }}><FileText size={18} /><span className="font-ui text-xs font-bold uppercase tracking-wide">{t("ws_field_lyrics")}</span></div>
          <h1 className={`${lang === "si" ? "font-si" : "font-en"} text-3xl font-bold`} style={{ color: "var(--text)" }}>{proj.title}</h1>
          <p className="font-ui text-sm mt-1" style={{ color: "var(--text-muted)" }}>{t("ws_by")} {proj.author_name}</p>
          <p className="font-si text-lg mt-4 whitespace-pre-line leading-relaxed" style={{ color: "var(--text-2)" }}>{proj.lyrics}</p>
        </div>

        {/* Melodies */}
        <section className="mb-8">
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-ui text-xl font-bold flex items-center gap-2" style={{ color: "var(--text)" }}><Music2 size={20} /> {t("ws_melodies")} ({proj.melodies.length})</h2>
            <button onClick={() => open("melody")} data-testid="add-melody-button" className="inline-flex items-center gap-2 h-11 px-4 rounded-full font-ui font-bold text-sm" style={{ background: "var(--brass-light)", color: "var(--accent)" }}><Plus size={16} /> {t("ws_add_melody")}</button>
          </div>
          {proj.melodies.length === 0 ? <p className="font-ui text-sm" style={{ color: "var(--text-muted)" }}>{t("ws_none_melody")}</p> : (
            <div className="space-y-2">
              {proj.melodies.map((m) => (
                <div key={m.id} data-testid={`melody-${m.id}`} className="rounded-2xl p-3" style={{ background: "var(--card)", border: "1px solid var(--border)" }}>
                  <p className="font-ui font-bold text-sm" style={{ color: "var(--text)" }}>{m.name}{m.notes ? <span className="font-normal" style={{ color: "var(--text-muted)" }}> — {m.notes}</span> : ""}</p>
                  <audio src={m.stream_url} controls preload="none" className="w-full mt-2" />
                </div>
              ))}
            </div>
          )}
        </section>

        {/* Vocals */}
        <section>
          <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
            <h2 className="font-ui text-xl font-bold flex items-center gap-2" style={{ color: "var(--text)" }}><Mic2 size={20} /> {t("ws_vocals")} ({proj.vocals.length})</h2>
            <div className="flex gap-2">
              <button onClick={() => open("sing")} disabled={proj.melodies.length === 0} data-testid="sing-button" className="inline-flex items-center gap-2 h-11 px-4 rounded-full font-ui font-bold text-sm disabled:opacity-50" style={{ background: "var(--accent)", color: "#fff" }}><Mic2 size={16} /> {t("ws_sing")}</button>
              <button onClick={() => open("upload")} data-testid="upload-take-button" className="inline-flex items-center gap-2 h-11 px-4 rounded-full font-ui font-bold text-sm" style={{ background: "var(--brass-light)", color: "var(--accent)" }}><Upload size={16} /> {t("ws_upload_take")}</button>
            </div>
          </div>
          {proj.vocals.length === 0 ? <p className="font-ui text-sm" style={{ color: "var(--text-muted)" }}>{t("ws_none_vocal")}</p> : (
            <div className="space-y-2">
              {proj.vocals.map((v) => (
                <div key={v.id} data-testid={`vocal-${v.id}`} className="rounded-2xl p-3" style={{ background: "var(--card)", border: "1px solid var(--border)" }}>
                  <div className="flex items-center justify-between gap-2">
                    <p className="font-ui font-bold text-sm" style={{ color: "var(--text)" }}>{v.name}</p>
                    <button onClick={() => setKaraoke(v)} data-testid={`karaoke-button-${v.id}`} className="shrink-0 inline-flex items-center gap-1.5 h-9 px-3 rounded-full font-ui font-bold text-xs" style={{ background: "var(--brass-light)", color: "var(--accent)" }}><Mic2 size={14} /> {t("kar_sing")}</button>
                  </div>
                  <audio src={v.stream_url} controls preload="none" className="w-full mt-2" />
                </div>
              ))}
            </div>
          )}
        </section>
      </div>

      {gate && <OtpGate onVerified={onVerified} onClose={() => { setGate(false); setPending(null); }} />}
      {action === "melody" && <MelodyModal pid={id} token={token} onExpire={() => onExpire("melody")} onClose={() => setAction(null)} onDone={() => { setAction(null); load(); }} />}
      {action === "sing" && <SingModal pid={id} token={token} melodies={proj.melodies} onExpire={() => onExpire("sing")} onClose={() => setAction(null)} onDone={() => { setAction(null); load(); }} />}
      {action === "upload" && <UploadTakeModal pid={id} token={token} melodies={proj.melodies} onExpire={() => onExpire("upload")} onClose={() => setAction(null)} onDone={() => { setAction(null); load(); }} />}
      {karaoke && <KaraokeOverlay lyrics={proj.lyrics} vocal={karaoke} onClose={() => setKaraoke(null)} />}
    </div>
  );
}

function KaraokeOverlay({ lyrics, vocal, onClose }) {
  const { t, lang } = useLang();
  const audioRef = useRef(null);
  const wrapRef = useRef(null);
  const innerRef = useRef(null);
  const [pct, setPct] = useState(0);

  useEffect(() => {
    const a = audioRef.current;
    const onT = () => { if (a.duration) setPct(a.currentTime / a.duration); };
    a?.addEventListener("timeupdate", onT);
    a?.play().catch(() => {});
    const h = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", h);
    return () => { a?.removeEventListener("timeupdate", onT); window.removeEventListener("keydown", h); };
  }, [onClose]);

  let translate = 0;
  if (wrapRef.current && innerRef.current) {
    const max = Math.max(0, innerRef.current.scrollHeight - wrapRef.current.clientHeight);
    translate = -(pct * max);
  }

  return (
    <div className="fixed inset-0 z-50 flex flex-col sk-scale-in" style={{ background: "var(--player-bg)" }} data-testid="karaoke-overlay">
      <div className="flex items-center justify-between px-5 py-4">
        <span className="font-ui text-sm font-bold tracking-widest uppercase" style={{ color: "var(--player-accent)" }}>{t("kar_title")} · {vocal.name}</span>
        <button onClick={onClose} data-testid="karaoke-close" className="grid place-items-center w-11 h-11 rounded-full" style={{ background: "rgba(255,255,255,0.1)", color: "#FFFDF7" }}><X size={20} /></button>
      </div>
      <div ref={wrapRef} className="flex-1 overflow-hidden px-6 relative" style={{ maskImage: "linear-gradient(180deg, transparent, #000 20%, #000 80%, transparent)", WebkitMaskImage: "linear-gradient(180deg, transparent, #000 20%, #000 80%, transparent)" }}>
        <div ref={innerRef} className={`${lang === "si" ? "font-si" : "font-en"} whitespace-pre-line text-center text-2xl sm:text-3xl leading-loose py-[30vh]`} style={{ color: "#FFFDF7", transform: `translateY(${translate}px)`, transition: "transform 0.6s linear" }}>
          {lyrics}
        </div>
      </div>
      <div className="p-4"><audio ref={audioRef} src={vocal.stream_url} controls autoPlay className="w-full" /></div>
    </div>
  );
}

const Shell = ({ title, children, onClose }) => {
  useEffect(() => {
    const h = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [onClose]);
  return (
    <div className="fixed inset-0 z-50 grid place-items-center px-4 py-8 overflow-y-auto" style={{ background: "rgba(0,0,0,0.5)" }} onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="w-full max-w-lg rounded-3xl p-6 space-y-4 sk-scale-in" style={{ background: "var(--card)", border: "2px solid var(--border)" }}>
        <div className="flex items-center justify-between">
          <h3 className="font-ui text-xl font-bold" style={{ color: "var(--text)" }}>{title}</h3>
          <button onClick={onClose} aria-label="Close" className="grid place-items-center w-10 h-10 rounded-full" style={{ background: "var(--bg)", color: "var(--text-muted)" }}><X size={18} /></button>
        </div>
        {children}
      </div>
    </div>
  );
};

const ConsentBox = ({ checked, onChange, tid }) => {
  const { t } = useLang();
  return (
    <label className="flex items-start gap-3 cursor-pointer rounded-2xl p-4" style={{ background: "var(--brass-light)" }}>
      <input data-testid={tid} type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} className="w-6 h-6 mt-0.5 accent-amber-600 shrink-0" />
      <span className="font-ui text-sm leading-relaxed" style={{ color: "var(--text)" }}>{t("consent_label")}</span>
    </label>
  );
};

function MelodyModal({ pid, token, onClose, onDone, onExpire }) {
  const { t } = useLang();
  const [name, setName] = useState(""); const [notes, setNotes] = useState("");
  const [file, setFile] = useState(null); const [consent, setConsent] = useState(false); const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (!file) { toast.error("Choose an audio file"); return; }
    if (!consent) { toast.error(t("noncommercial_note")); return; }
    const fd = new FormData();
    fd.append("token", token); fd.append("name", name); fd.append("notes", notes); fd.append("consent", "true"); fd.append("audio", file);
    setBusy(true);
    try { await axios.post(`${API}/workshop/projects/${pid}/melodies`, fd, { headers: { "Content-Type": "multipart/form-data" } }); toast.success("Melody added"); onDone(); }
    catch (err) { if (err.response?.status === 401) { onExpire?.(); return; } toast.error(fmtErr(err.response?.data?.detail)); } finally { setBusy(false); }
  };
  return (
    <Shell title={t("ws_add_melody")} onClose={onClose}>
      <form onSubmit={submit} className="space-y-4" data-testid="melody-form">
        <label className="block"><span className="font-ui text-sm font-bold" style={{ color: "var(--text-2)" }}>{t("ws_composer")} *</span>
          <input data-testid="melody-name" required value={name} onChange={(e) => setName(e.target.value)} className="mt-1 w-full h-12 px-4 rounded-xl font-ui outline-none" style={inputStyle} /></label>
        <label className="block"><span className="font-ui text-sm font-bold" style={{ color: "var(--text-2)" }}>{t("ws_field_tags")}</span>
          <input data-testid="melody-notes" value={notes} onChange={(e) => setNotes(e.target.value)} className="mt-1 w-full h-12 px-4 rounded-xl font-ui outline-none" style={inputStyle} /></label>
        <label className="block"><span className="font-ui text-sm font-bold" style={{ color: "var(--text-2)" }}>{t("ws_instrumental")} *</span>
          <input data-testid="melody-file" type="file" accept="audio/*" required onChange={(e) => setFile(e.target.files[0])} className="mt-1 w-full font-ui text-sm" style={{ color: "var(--text-2)" }} /></label>
        <ConsentBox checked={consent} onChange={setConsent} tid="melody-consent" />
        <button data-testid="melody-submit" disabled={busy} type="submit" className="w-full h-12 rounded-xl font-ui font-bold flex items-center justify-center gap-2 disabled:opacity-60" style={{ background: "var(--accent)", color: "#fff" }}>{busy ? <Loader2 size={18} className="animate-spin" /> : <Plus size={18} />} {t("ws_add_melody")}</button>
      </form>
    </Shell>
  );
}

function SingModal({ pid, token, melodies, onClose, onDone, onExpire }) {
  const { t } = useLang();
  const [melodyId, setMelodyId] = useState(melodies[0]?.id || "");
  const [name, setName] = useState(""); const [consent, setConsent] = useState(false);
  const [mixBlob, setMixBlob] = useState(null); const [busy, setBusy] = useState(false);
  const melody = melodies.find((m) => m.id === melodyId);
  const submit = async (e) => {
    e.preventDefault();
    if (!mixBlob) { toast.error("Please record and mix your take first."); return; }
    if (!consent) { toast.error(t("noncommercial_note")); return; }
    const fd = new FormData();
    fd.append("token", token); fd.append("name", name); fd.append("melody_id", melodyId); fd.append("consent", "true");
    fd.append("audio", new File([mixBlob], "take.wav", { type: "audio/wav" }));
    setBusy(true);
    try { await axios.post(`${API}/workshop/projects/${pid}/vocals`, fd, { headers: { "Content-Type": "multipart/form-data" } }); toast.success("Take published"); onDone(); }
    catch (err) { if (err.response?.status === 401) { onExpire?.(); return; } toast.error(fmtErr(err.response?.data?.detail)); } finally { setBusy(false); }
  };
  return (
    <Shell title={t("ws_sing")} onClose={onClose}>
      <form onSubmit={submit} className="space-y-4" data-testid="sing-form">
        <label className="block"><span className="font-ui text-sm font-bold" style={{ color: "var(--text-2)" }}>{t("ws_pick_melody")}</span>
          <select data-testid="sing-melody-select" value={melodyId} onChange={(e) => setMelodyId(e.target.value)} className="mt-1 w-full h-12 px-4 rounded-xl font-ui outline-none" style={inputStyle}>
            {melodies.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
          </select></label>
        {melody && <VocalRecorder key={melodyId} melodyUrl={melody.stream_url} onMixed={setMixBlob} />}
        <label className="block"><span className="font-ui text-sm font-bold" style={{ color: "var(--text-2)" }}>{t("ws_singer")} *</span>
          <input data-testid="sing-name" required value={name} onChange={(e) => setName(e.target.value)} className="mt-1 w-full h-12 px-4 rounded-xl font-ui outline-none" style={inputStyle} /></label>
        <ConsentBox checked={consent} onChange={setConsent} tid="sing-consent" />
        <button data-testid="sing-submit" disabled={busy || !mixBlob} type="submit" className="w-full h-12 rounded-xl font-ui font-bold flex items-center justify-center gap-2 disabled:opacity-60" style={{ background: "var(--accent)", color: "#fff" }}>{busy ? <Loader2 size={18} className="animate-spin" /> : <Mic2 size={18} />} {t("ws_publish_take")}</button>
      </form>
    </Shell>
  );
}

function UploadTakeModal({ pid, token, melodies, onClose, onDone, onExpire }) {
  const { t } = useLang();
  const [melodyId, setMelodyId] = useState("");
  const [name, setName] = useState(""); const [file, setFile] = useState(null); const [consent, setConsent] = useState(false); const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (!file) { toast.error("Choose an audio file"); return; }
    if (!consent) { toast.error(t("noncommercial_note")); return; }
    const fd = new FormData();
    fd.append("token", token); fd.append("name", name); fd.append("melody_id", melodyId); fd.append("consent", "true"); fd.append("audio", file);
    setBusy(true);
    try { await axios.post(`${API}/workshop/projects/${pid}/vocals`, fd, { headers: { "Content-Type": "multipart/form-data" } }); toast.success("Take published"); onDone(); }
    catch (err) { if (err.response?.status === 401) { onExpire?.(); return; } toast.error(fmtErr(err.response?.data?.detail)); } finally { setBusy(false); }
  };
  return (
    <Shell title={t("ws_upload_take")} onClose={onClose}>
      <form onSubmit={submit} className="space-y-4" data-testid="upload-take-form">
        {melodies.length > 0 && (
          <label className="block"><span className="font-ui text-sm font-bold" style={{ color: "var(--text-2)" }}>{t("ws_pick_melody")}</span>
            <select data-testid="uploadtake-melody-select" value={melodyId} onChange={(e) => setMelodyId(e.target.value)} className="mt-1 w-full h-12 px-4 rounded-xl font-ui outline-none" style={inputStyle}>
              <option value="">—</option>
              {melodies.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
            </select></label>
        )}
        <label className="block"><span className="font-ui text-sm font-bold" style={{ color: "var(--text-2)" }}>{t("ws_singer")} *</span>
          <input data-testid="uploadtake-name" required value={name} onChange={(e) => setName(e.target.value)} className="mt-1 w-full h-12 px-4 rounded-xl font-ui outline-none" style={inputStyle} /></label>
        <label className="block"><span className="font-ui text-sm font-bold" style={{ color: "var(--text-2)" }}>{t("f_audio")} *</span>
          <input data-testid="uploadtake-file" type="file" accept="audio/*" required onChange={(e) => setFile(e.target.files[0])} className="mt-1 w-full font-ui text-sm" style={{ color: "var(--text-2)" }} /></label>
        <ConsentBox checked={consent} onChange={setConsent} tid="uploadtake-consent" />
        <button data-testid="uploadtake-submit" disabled={busy} type="submit" className="w-full h-12 rounded-xl font-ui font-bold flex items-center justify-center gap-2 disabled:opacity-60" style={{ background: "var(--accent)", color: "#fff" }}>{busy ? <Loader2 size={18} className="animate-spin" /> : <Upload size={18} />} {t("ws_publish_take")}</button>
      </form>
    </Shell>
  );
}
